import { useState } from 'react'
import { downloadFile } from '../api/client'
import {
    entryQueryString,
    useClients,
    useProjects,
    useTimeEntries,
    type EntryFilters,
} from '../hooks/useApi'
import {
    formatDate,
    formatDurationCompact,
    formatMoney,
    formatTime,
} from '../lib/format'
import { useT } from '../i18n/context'
import { toast } from '../stores/toast'
import type { TimeEntry, TimeEntryType } from '../types/api'
import { Badge } from './ui/Badge'
import { entryTypeMeta } from '../lib/labelMaps'
import { Button } from './ui/Button'
import { EmptyState } from './ui/EmptyState'
import { Field, Select } from './ui/Field'
import { PageLoader } from './ui/Spinner'
import { EntryDialog } from './EntryDialog'

/**
 * Geteilte Einträge-Bausteine für die Abrechnung („Einträge"-Tab, mit
 * Von/Bis-Filter) und die Zeiterfassung (Monatskarte unter „Zeitraum
 * manuell erfassen").
 */

/** Filter-Dropdowns Projekt/Kunde/Typ. */
export function EntryFilterSelects({
    workspaceId,
    filters,
    onPatch,
}: {
    workspaceId: number | null
    filters: EntryFilters
    onPatch: (patch: Partial<EntryFilters>) => void
}) {
    const t = useT()
    const clients = useClients(workspaceId)
    const projects = useProjects(workspaceId)

    return (
        <>
            <Field label={t('entries.project')}>
                <Select
                    value={filters.projectId ?? ''}
                    onChange={e =>
                        onPatch({
                            projectId: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                        })
                    }
                    className='w-48'
                >
                    <option value=''>{t('common.all')}</option>
                    {(projects.data ?? []).map(p => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </Select>
            </Field>
            <Field label={t('entries.client')}>
                <Select
                    value={filters.clientId ?? ''}
                    onChange={e =>
                        onPatch({
                            clientId: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                        })
                    }
                    className='w-48'
                >
                    <option value=''>{t('common.all')}</option>
                    {(clients.data ?? []).map(c => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </Select>
            </Field>
            <Field label={t('track.type')}>
                <Select
                    value={filters.entryType ?? ''}
                    onChange={e =>
                        onPatch({
                            entryType: (e.target.value ||
                                undefined) as TimeEntryType | undefined,
                        })
                    }
                    className='w-36'
                >
                    <option value=''>{t('common.all')}</option>
                    {Object.entries(entryTypeMeta).map(([value, meta]) => (
                        <option key={value} value={value}>
                            {t(meta.labelKey)}
                        </option>
                    ))}
                </Select>
            </Field>
        </>
    )
}

/** CSV-Export der Einträge mit den aktuell gesetzten Filtern. */
export function EntriesCsvButton({
    workspaceId,
    filters,
    className = '',
}: {
    workspaceId: number
    filters: EntryFilters
    className?: string
}) {
    const t = useT()
    return (
        <Button
            variant='secondary'
            className={className}
            onClick={() => {
                const qs = entryQueryString(workspaceId, {
                    ...filters,
                    page: undefined,
                    limit: undefined,
                })
                downloadFile(
                    `/time-entries/export?${qs}`,
                    t('entries.csvName'),
                ).catch(() => toast.error(t('common.exportFailed')))
            }}
        >
            {t('entries.csvExport')}
        </Button>
    )
}

/** Einträge-Tabelle mit Pagination und Bearbeiten-Dialog. */
export function EntriesTable({
    workspaceId,
    filters,
    onPage,
}: {
    workspaceId: number | null
    filters: EntryFilters
    onPage: (page: number) => void
}) {
    const t = useT()
    const entries = useTimeEntries(workspaceId, filters)
    const clients = useClients(workspaceId)
    const projects = useProjects(workspaceId)
    const [editing, setEditing] = useState<TimeEntry | null>(null)

    const clientName = (id: number | null) =>
        id === null
            ? ''
            : (clients.data ?? []).find(c => c.id === id)?.name ?? `#${id}`
    const projectName = (id: number | null) =>
        id === null
            ? ''
            : (projects.data ?? []).find(p => p.id === id)?.name ?? `#${id}`

    return (
        <>
            {entries.isLoading ? (
                <PageLoader />
            ) : (entries.data?.data.length ?? 0) === 0 ? (
                <EmptyState
                    title={t('entries.emptyTitle')}
                    description={t('entries.emptyDesc')}
                />
            ) : (
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-slate-200 dark:border-slate-700 text-left text-xs tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                                <th scope='col' className='px-4 py-2.5'>
                                    {t('entries.date')}
                                </th>
                                <th scope='col' className='px-4 py-2.5'>
                                    {t('track.description')}
                                </th>
                                <th scope='col' className='px-4 py-2.5'>
                                    {t('track.projectClient')}
                                </th>
                                <th scope='col' className='px-4 py-2.5'>
                                    {t('track.type')}
                                </th>
                                <th
                                    scope='col'
                                    className='px-4 py-2.5 text-right'
                                >
                                    {t('entries.duration')}
                                </th>
                                <th
                                    scope='col'
                                    className='px-4 py-2.5 text-right'
                                >
                                    {t('entries.yield')}
                                </th>
                                {/* Leere Spaltenköpfe gelten axe als fehlender
                                    Header für die Bearbeiten-Zelle (td-has-header) */}
                                <th scope='col' className='px-4 py-2.5'>
                                    <span className='sr-only'>
                                        {t('entries.actions')}
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.data!.data.map(entry => (
                                <tr
                                    key={entry.id}
                                    className='border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-800/60'
                                >
                                    <td className='px-4 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400'>
                                        {formatDate(entry.startTime)}
                                        <span className='ml-1.5 text-xs text-slate-500 dark:text-slate-400'>
                                            {formatTime(entry.startTime)}
                                            {entry.endTime
                                                ? `–${formatTime(entry.endTime)}`
                                                : ''}
                                        </span>
                                    </td>
                                    <td className='max-w-64 truncate px-4 py-2.5 text-slate-800 dark:text-slate-100'>
                                        {entry.description || (
                                            <span className='text-slate-300 dark:text-slate-600 italic'>
                                                —
                                            </span>
                                        )}
                                        {!entry.endTime && (
                                            <span className='ml-2 inline-flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400'>
                                                <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-red-500' />
                                                {t('entries.running')}
                                            </span>
                                        )}
                                    </td>
                                    <td className='px-4 py-2.5 text-slate-600 dark:text-slate-300'>
                                        {projectName(entry.projectId) && (
                                            <>
                                                {projectName(
                                                    entry.projectId,
                                                )}
                                            </>
                                        )}
                                        {projectName(entry.projectId) &&
                                            clientName(entry.clientId) &&
                                            ' · '}
                                        {clientName(entry.clientId)}
                                    </td>
                                    <td className='px-4 py-2.5'>
                                        <Badge
                                            tone={
                                                entryTypeMeta[
                                                    entry.entryType
                                                ].tone
                                            }
                                        >
                                            {t(
                                                entryTypeMeta[
                                                    entry.entryType
                                                ].labelKey,
                                            )}
                                        </Badge>
                                    </td>
                                    <td className='px-4 py-2.5 text-right font-medium tabular-nums text-slate-700 dark:text-slate-200'>
                                        {entry.endTime
                                            ? formatDurationCompact(
                                                  entry.duration ?? 0,
                                              )
                                            : '—'}
                                    </td>
                                    <td className='px-4 py-2.5 text-right tabular-nums text-slate-500 dark:text-slate-400'>
                                        {entry.isBillable &&
                                        entry.hourlyRate !== null &&
                                        entry.endTime
                                            ? formatMoney(
                                                  ((entry.duration ??
                                                      0) /
                                                      3600) *
                                                      entry.hourlyRate,
                                              )
                                            : entry.isBillable
                                              ? t('entries.open')
                                              : '—'}
                                    </td>
                                    <td className='px-4 py-2.5 text-right whitespace-nowrap'>
                                        <Button
                                            variant='ghost'
                                            size='sm'
                                            onClick={() => setEditing(entry)}
                                        >
                                            {t('common.edit')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {entries.data && entries.data.meta.totalPages > 1 && (
                <div className='flex items-center justify-between border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400'>
                    <span>
                        {t('entries.pageInfo', {
                            page: entries.data.meta.page,
                            totalPages: entries.data.meta.totalPages,
                            count: entries.data.meta.total,
                        })}
                    </span>
                    <div className='flex gap-1'>
                        <Button
                            variant='secondary'
                            size='sm'
                            disabled={
                                (filters.page ?? 1) <= 1 || entries.isFetching
                            }
                            onClick={() => onPage((filters.page ?? 1) - 1)}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant='secondary'
                            size='sm'
                            disabled={
                                (filters.page ?? 1) >=
                                    entries.data.meta.totalPages ||
                                entries.isFetching
                            }
                            onClick={() => onPage((filters.page ?? 1) + 1)}
                        >
                            {t('common.next')}
                        </Button>
                    </div>
                </div>
            )}

            <EntryDialog
                open={editing !== null}
                entry={editing}
                onClose={() => setEditing(null)}
                workspaceId={workspaceId!}
            />
        </>
    )
}
